export const AUDIO_MODES = ["narrated", "silent", "none"]
// Reading pace: a silent mode, or a part that could not be voiced, holds the chapter for as long as it takes to read it
// (and the tour then waits for Next rather than advancing on its own).
export const READING_WPS = 2.7
export const READING_MIN_S = 4
/**
 * Clips fetched ahead at once (kraftware-dev#129). The tour hands prefetch() its clips in tour order from the visitor's
 * chapter; two at a time keeps the next one coming without the other 20-odd competing with the clip about to play, and
 * nothing new starts while a clip the tour is waiting for (play / whenReady) is still loading.
 */
export const PREFETCH_CONCURRENCY = 2
/** How long a chapter waits for its clip to arrive ("Loading narration…") before it is read instead. Paused time does not count. */
export const CLIP_LOAD_TIMEOUT_MS = 20000
/** Time to read `text` at READING_WPS words per second, never under READING_MIN_S. */
export function readingMs(text) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean).length
  return Math.round(Math.max(READING_MIN_S, words / READING_WPS) * 1000)
}

/** A 50 ms silent WAV as an object URL: what the first tap plays to unlock the shared element (see unlock). */
function silentWavUrl() {
  const rate = 8000, n = 400, buf = new ArrayBuffer(44 + n), d = new DataView(buf)
  const str = (o, s) => { for (let i = 0; i < s.length; i++) d.setUint8(o + i, s.charCodeAt(i)) }
  str(0, "RIFF"); d.setUint32(4, 36 + n, true); str(8, "WAVE"); str(12, "fmt "); d.setUint32(16, 16, true)
  d.setUint16(20, 1, true); d.setUint16(22, 1, true); d.setUint32(24, rate, true); d.setUint32(28, rate, true)
  d.setUint16(32, 1, true); d.setUint16(34, 8, true); str(36, "data"); d.setUint32(40, n, true)
  for (let i = 0; i < n; i++) d.setUint8(44 + i, 128)   // 8-bit PCM silence is the midpoint
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }))
}

/**
 * Browsers only let a page start sound from a visitor's tap or click. Chrome remembers a tap for the whole page, but
 * Safari (every browser on an iPhone) allows only a media element that was itself started inside a tap, and a fresh
 * `new Audio()` played after a network fetch is refused there. So this backend keeps ONE element for every clip,
 * starts it inside the first tap (unlock), and reuses it: later clips play on an element the browser already trusts.
 */
function browserBackend() {
  let ctx = null, el = null, utter = null, unlocked = false, playToken = 0
  let held = false, heldClip = false   // pause(): the visitor stepped away; the clip waits on the element, where it was
  const clips = new Map()   // clip url -> Promise<object URL>: each clip is fetched whole once (see play)
  const ready = new Set(), failed = new Set()   // settled fetches (a failed one leaves `clips` and is retried on its next play)
  const inflight = new Set()                    // fetches on the wire
  const wanted = new Set()                      // clips the tour is waiting on right now: while one loads, no prefetch starts
  const tracked = new Set()                     // every clip ever handed to prefetch(): the n of N the Guide shows
  const progressFns = new Set()
  let queue = []                                // prefetch order (tour order from the visitor's chapter), not yet started
  let silence = null
  const media = () => {
    if (!el) {
      el = new Audio(); el.preload = "auto"; el.setAttribute("playsinline", "")
      el.addEventListener("playing", () => { unlocked = true })   // the browser trusts this element from now on
    }
    return el
  }
  function emitProgress() { const p = be.progress(); for (const fn of progressFns) { try { fn(p) } catch (_) { /* a listener must not stop the queue */ } } }
  function fetchClip(url, { want = false } = {}) {
    if (want && !ready.has(url)) wanted.add(url)
    if (!clips.has(url)) {
      queue = queue.filter((u) => u !== url)
      inflight.add(url); failed.delete(url)
      const p = fetch(url).then((r) => { if (!r.ok) throw new Error("http " + r.status); return r.blob() })
        .then((blob) => { if (blob.size < 512) throw new Error("empty clip"); return URL.createObjectURL(blob) })
      const settle = () => { inflight.delete(url); wanted.delete(url); emitProgress(); pump() }
      p.then(() => { ready.add(url); settle() }, () => { clips.delete(url); failed.add(url); settle() })   // a failed fetch is retried on the next play, never cached
      clips.set(url, p)
    }
    return clips.get(url)
  }
  /** Start queued prefetches up to PREFETCH_CONCURRENCY, or none while a clip the tour waits for is still loading. */
  function pump() {
    const waiting = [...wanted].some((u) => inflight.has(u))
    const limit = waiting ? 1 : PREFETCH_CONCURRENCY
    while (queue.length && inflight.size < limit) {
      const u = queue.shift()
      if (!clips.has(u) && !failed.has(u)) fetchClip(u).catch(() => {})
    }
  }
  const be = {
    /**
     * Call from inside a tap or click. Starts the shared element (50 ms of silence) and resumes the chime context, so
     * every later clip may play without another tap. Cheap and idempotent; it makes no audible sound.
     */
    unlock() {
      try {
        const a = media()
        if (!unlocked && a.paused) {   // never interrupts a clip; once one has played, the element needs no unlock
          a.src = silence || (silence = silentWavUrl())
          const p = a.play()
          if (p && p.then) p.then(() => { unlocked = true }, () => {})
        }
        const AC = window.AudioContext || window.webkitAudioContext
        if (AC) { if (!ctx) ctx = new AC(); if (ctx.state === "suspended") ctx.resume().catch(() => {}) }
      } catch (_) {}
    },
    /**
     * Fetch clips ahead of need, in the order given (the tour's order from the visitor's chapter), PREFETCH_CONCURRENCY at
     * a time, so a chapter's narration starts from memory when the visitor reaches it. A newer call puts its clips first.
     * The queue starts after the current task, so a play() in the same tap claims its clip before any prefetch does.
     */
    prefetch(urls) {
      const list = [...new Set((urls || []).filter(Boolean))]
      for (const u of list) tracked.add(u)
      const fresh = list.filter((u) => !clips.has(u))
      for (const u of fresh) failed.delete(u)
      queue = [...fresh, ...queue.filter((u) => !fresh.includes(u))]
      emitProgress()
      Promise.resolve().then(pump)
    },
    /** True once the clip is in memory (play() starts it at once). */
    isReady(url) { return ready.has(url) },
    /** The clip, fetched now if it is not loading yet; while the tour waits on it no other prefetch starts. */
    whenReady(url) { return fetchClip(url, { want: true }) },
    /** How far the prefetch is: { ready, failed, total } over every clip handed to prefetch(). */
    progress() {
      let r = 0, f = 0
      for (const u of tracked) { if (ready.has(u)) r++; else if (failed.has(u)) f++ }
      return { ready: r, failed: f, total: tracked.size }
    },
    /** fn(progress()) whenever a clip settles or the queue changes; returns an unsubscribe. */
    onProgress(fn) { progressFns.add(fn); return () => progressFns.delete(fn) },
    chime(freq, dur) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return
        if (!ctx) ctx = new AC()
        if (ctx.state === "suspended") ctx.resume().catch(() => {})
        const o = ctx.createOscillator(), g = ctx.createGain()
        o.frequency.setValueAtTime(freq, ctx.currentTime); g.gain.setValueAtTime(0.05, ctx.currentTime)
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur); o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + dur)
      } catch (_) {}
    },
    /**
     * Play a clip from memory on the shared element. The file is fetched whole first (10–150 KB) and played from an
     * object URL, so nothing depends on range requests through the CDN or a stall mid-stream (the "plays a second,
     * then stops" failure, #123). A newer play() supersedes an older one. Any failure is recorded in `lastError`.
     */
    play(url) {
      const my = ++playToken
      return new Promise((resolve, reject) => {
        const fail = (why, extra) => { be.lastError = Object.assign({ url, why, at: new Date().toISOString() }, extra || {}); reject(new Error(why)) }
        fetchClip(url, { want: true }).then((src) => {
          if (my !== playToken) return resolve()   // superseded (the visitor moved on) while the clip loaded
          const a = media()
          const off = () => { a.removeEventListener("ended", onEnd); a.removeEventListener("error", onErr) }
          const onEnd = () => { off(); resolve() }
          const onErr = () => { off(); const code = a.error && a.error.code; fail("media error " + code, { code, t: a.currentTime }) }
          a.addEventListener("ended", onEnd); a.addEventListener("error", onErr)
          be.current = { off, resolve }
          a.src = src
          a.currentTime = 0
          if (held) { heldClip = true; return }   // loaded while paused: it starts on resume(), from the visitor's tap
          const p = a.play()
          if (p && p.catch) p.catch((e) => { off(); fail("play rejected: " + (e && e.name), { t: 0 }) })
        }).catch((e) => fail(String(e && e.message || e)))
      })
    },
    /** Hold the clip (or the browser speech) where it is; the play() promise stays pending until it really ends. */
    pause() {
      held = true
      try {
        if (el && be.current && !el.paused) { el.pause(); heldClip = true }
        if (utter && window.speechSynthesis) window.speechSynthesis.pause()
      } catch (_) {}
    },
    /** Continue from where pause() held it, on the same element: call it from a tap, like every start (iPhone). */
    resume() {
      held = false
      try {
        if (heldClip && el && be.current) {
          heldClip = false
          const p = el.play()
          if (p && p.catch) p.catch((e) => { be.lastError = { url: el.src, why: "resume rejected: " + (e && e.name), at: new Date().toISOString(), t: el.currentTime } })
        }
        heldClip = false
        if (window.speechSynthesis && window.speechSynthesis.paused) window.speechSynthesis.resume()
      } catch (_) {}
    },
    /** Seconds into the clip that is playing (or held), or null when no clip is current. */
    position() { return el && be.current ? el.currentTime : null },
    /** The current clip's length in seconds, once its metadata has loaded; null otherwise. */
    duration() { return el && be.current && Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null },
    current: null,
    lastError: null,
    /** Browser speech for a part with no clip (the script is not recorded yet): resolves at the end of the utterance. */
    say(text) {
      return new Promise((resolve, reject) => {
        const ss = window.speechSynthesis, U = window.SpeechSynthesisUtterance
        if (!ss || !U) return reject(new Error("no speech synthesis"))
        const u = new U(text); u.rate = 1.03; u.lang = "en-US"
        // Chrome quirks: an utterance that is not referenced can be collected mid-sentence (it goes silent), and a
        // cancel() issued right before speak() drops the new utterance. Keep the reference; never cancel here.
        utter = u
        u.addEventListener("end", () => { if (utter === u) utter = null; resolve() })
        u.addEventListener("error", (e) => { if (utter === u) utter = null; if (e && e.error === "interrupted") return resolve(); be.lastError = { why: "speech " + (e && e.error), at: new Date().toISOString() }; reject(new Error("speech failed: " + (e && e.error))) })
        try { ss.speak(u) } catch (e) { reject(e) }
      })
    },
    stop() {
      held = false; heldClip = false
      try {
        playToken++   // a clip still loading never starts
        if (el && !el.paused) el.pause()
        if (be.current) { const c = be.current; be.current = null; c.off(); c.resolve() }
        if (utter && window.speechSynthesis) { utter = null; window.speechSynthesis.cancel() }
      } catch (_) {}
    },
  }
  return be
}

/**
 * Alarm sound (consent-gated through audio.chime, which is silent unless the mode is narrated or silent): a warning
 * chimes once at 440 Hz, an alarm twice at 330 Hz 120 ms apart. Only alarm.raised sounds; updates, acks and clears are
 * quiet. The host registers this AFTER the backfill so eight hours of history never ring.
 */
export function annunciateAlarms(model, audio, later = (fn, ms) => setTimeout(fn, ms)) {
  return model.onEvent((e) => {
    if (e.type !== "alarm.raised") return
    if (e.severity === "alarm") { audio.chime(330, 0.2); later(() => audio.chime(330, 0.2), 120) }
    else audio.chime(440, 0.12)
  })
}

export function createAudio({ mode = "none", backend } = {}) {
  const be = backend || browserBackend()
  // Each speak() is a generation: a newer call (the next chapter) makes the older loop stale, so it never plays its
  // remaining clips (the previous chapter's question) and never touches the newer call's state.
  let gen = 0
  let active = null    // the latest call: { abandon } ends the clip it is playing (stop / sound switched off)
  // ---- pause: the visitor stepped away from the tour (kraftware-dev, operator 2026-10-16: "explore and come right back
  // where they left off"). Everything that measures the chapter's time stops with it: the clip on its element, the
  // reading time of an unvoiced part, and the 2 × cap, so a long pause never counts as a stalled clip.
  let paused = false
  const waits = new Set()
  /** A delay that only counts unpaused time. `cancel()` drops it (the clip ended first). */
  function wait(ms) {
    const w = { left: Math.max(0, ms), timer: null, since: 0, done: null }
    w.promise = new Promise((resolve) => { w.done = resolve })
    w.arm = () => { w.since = Date.now(); w.timer = setTimeout(() => { waits.delete(w); w.done() }, w.left) }
    w.hold = () => { if (w.timer) { clearTimeout(w.timer); w.timer = null; w.left = Math.max(0, w.left - (Date.now() - w.since)) } }
    w.cancel = () => { if (w.timer) clearTimeout(w.timer); waits.delete(w) }
    waits.add(w)
    if (!paused) w.arm()
    return w
  }
  // The chapter's clock: which part is speaking and how far in. A clip reports its own time (it is what the visitor
  // hears); a read part, or browser speech, runs a stopwatch that stops while paused.
  const clock = { part: -1, kind: "idle", start: 0, held: 0, heldAt: null }
  function clockStart(part, kind) { Object.assign(clock, { part, kind, start: Date.now(), held: 0, heldAt: paused ? Date.now() : null }) }
  function elapsedS() { const now = Date.now(); return (now - clock.start - clock.held - (clock.heldAt != null ? now - clock.heldAt : 0)) / 1000 }
  /**
   * One part: its clip in narrated mode, else its reading time. A clip that fails (404: not recorded yet), is refused
   * (autoplay) or is cut off is read for the rest of its time. Capped at 2 × the reading time so a load that stalls
   * without ever firing "ended" or "error" cannot hold the tour's slow path.
   */
  async function segment({ text, mp3 }, my, call, part) {
    const read = readingMs(text), cap = 2 * read
    let t0 = Date.now()
    if (a.mode === "narrated" && my === gen) {
      // A clip still on the wire (a slow link): the chapter says "Loading narration…" and its clock (the highlight, the
      // camera cues, the 2 × cap) starts when the clip has arrived, not while it downloads. Up to CLIP_LOAD_TIMEOUT_MS of
      // unpaused time, then the part is read. Backends that cannot say (tests' fakes) keep the old path.
      const pending = !!mp3 && typeof be.isReady === "function" && typeof be.whenReady === "function" && !be.isReady(mp3)
      let capW = null, loadW = null
      const how = await new Promise((resolve) => {
        call.abandon = () => resolve("cut")
        const armCap = () => {
          if (capW || my !== gen) return
          if (loadW) { loadW.cancel(); loadW = null }
          setLoading(null)
          clockStart(part, mp3 ? "voice" : "read"); t0 = Date.now()
          capW = wait(cap); capW.promise.then(() => resolve("cap"))
        }
        if (pending) {
          clockStart(part, "loading"); setLoading({ url: mp3, part })
          loadW = wait(CLIP_LOAD_TIMEOUT_MS); loadW.promise.then(() => resolve("load-timeout"))
          be.whenReady(mp3).then(armCap, armCap)   // registered before play(): the clock starts as the clip does
        } else armCap()
        const voice = (mp3 ? be.play(mp3) : Promise.reject(new Error("no clip")))
          .catch(() => { if (my === gen) { armCap(); clockStart(part, "read") } return typeof be.say === "function" ? be.say(text) : Promise.reject(new Error("no speech")) })
        voice.then(() => resolve("ended"), () => resolve("failed"))
      })
      if (capW) capW.cancel()
      if (loadW) loadW.cancel()
      call.abandon = null
      if (my === gen) setLoading(null)
      if (how === "ended") return true
      if (how === "cap" || how === "cut") return false
      if (how === "load-timeout") {
        if (my !== gen) return false
        be.lastError = { url: mp3, why: "clip did not arrive in time", at: new Date().toISOString() }
        if (typeof be.stop === "function") be.stop()   // a clip that turns up later never starts over the reading
        clockStart(part, "read"); t0 = Date.now()
      }
    } else if (my === gen) clockStart(part, "read")
    await wait(Math.max(0, Math.min(read, cap - (Date.now() - t0)))).promise
    return false
  }
  // ---- narration readiness (kraftware-dev#129): which clip the chapter is waiting for, and how far the prefetch is ----
  let loading = null
  const statusFns = new Set()
  function status() {
    const p = typeof be.progress === "function" ? be.progress() : { ready: 0, failed: 0, total: 0 }
    return { loading: !!loading, part: loading ? loading.part : -1, url: loading ? loading.url : null, ready: p.ready, failed: p.failed, total: p.total }
  }
  function emitStatus() { const st = status(); for (const fn of statusFns) { try { fn(st) } catch (_) { /* a listener must not stop the narration */ } } }
  function setLoading(l) { if (loading === l || (!loading && !l)) return; loading = l; emitStatus() }
  if (typeof be.onProgress === "function") be.onProgress(emitStatus)
  const a = {
    mode,
    setMode(m) {
      if (!AUDIO_MODES.includes(m)) throw new Error("bad audio mode " + m)
      const prev = a.mode; a.mode = m
      if (prev === "narrated" && m !== "narrated") a.stop()
      // iPhone/iPad Safari 17+: the "playback" session keeps narration and chimes audible with the ring switch on silent.
      if (m === "narrated") { try { if (typeof navigator !== "undefined" && navigator.audioSession) navigator.audioSession.type = "playback" } catch (_) {} }
    },
    /** Call inside a tap or click: lets every later clip and chime play (see browserBackend). Silent. */
    unlock() { if (typeof be.unlock === "function") be.unlock() },
    /** Start loading clips (narrated mode only) so each chapter's voice plays from memory. */
    prefetch(urls) { if (a.mode === "narrated" && typeof be.prefetch === "function") be.prefetch(urls) },
    chime(freq = 520, dur = 0.08) { if (a.mode === "narrated" || a.mode === "silent") be.chime(freq, dur) },
    /**
     * One chapter: the narration with its contrast line (one clip, `mp3`), then the question (`questionMp3`, spec §7).
     * Resolves once when the whole sequence has ended (or when a newer speak() supersedes it). Each part plays its clip
     * in narrated mode; a part with no clip, a clip that fails or is cut off, and every part in sounds-only / silent mode
     * takes its reading time instead, so the tour's slow path always gets an end. A part whose clip cannot play falls
     * back to the browser's own speech (be.say) before it falls back to reading time.
     */
    async speak({ narration = "", contrast = "", question = "", mp3 = null, questionMp3 = null } = {}) {
      const my = ++gen, call = { abandon: null }
      active = call
      const parts = [{ text: [narration, contrast].filter(Boolean).join(" "), mp3 }, { text: question, mp3: questionMp3 }]
      let voiced = true, any = false
      for (let k = 0; k < parts.length; k++) {
        const p = parts[k]
        if (my !== gen) return { voiced: false }
        if (p.text) { any = true; voiced = (await segment(p, my, call, k)) && voiced }
      }
      if (my === gen) clockStart(-1, "idle")
      return { voiced: any && voiced }
    },
    /** Narration readiness: { loading, part, url, ready, failed, total } (loading: the chapter waits for its clip). */
    status,
    /** fn(status()) whenever a clip arrives or the chapter starts or stops waiting for one; returns an unsubscribe. */
    onStatus(fn) { statusFns.add(fn); return () => statusFns.delete(fn) },
    stop() {
      // A stop (the next chapter, sound switched off) also ends a pause: whatever plays next starts at once.
      if (paused) { paused = false; clock.heldAt = null; for (const w of waits) if (!w.timer) w.arm() }
      be.stop(); if (active && active.abandon) active.abandon()
      clockStart(-1, "idle"); setLoading(null)
    },
    /** The visitor stepped away: hold the chapter where it is (clip, reading time, cap). Idempotent. */
    pause() {
      if (paused) return
      paused = true; clock.heldAt = Date.now()
      for (const w of waits) w.hold()
      if (typeof be.pause === "function") be.pause()
    },
    /** Back to the tour: continue from where pause() held it. Call it from the visitor's tap (iPhone plays only then). */
    resume() {
      if (!paused) return
      paused = false
      if (clock.heldAt != null) { clock.held += Date.now() - clock.heldAt; clock.heldAt = null }
      for (const w of waits) if (!w.timer) w.arm()
      if (typeof be.resume === "function") be.resume()
    },
    get paused() { return paused },
    /**
     * Where the chapter's sound is: { part: 0 (narration + contrast) | 1 (question) | -1 (nothing), t: seconds into that
     * part, kind: "voice" (a recorded clip's own clock) | "read" (reading pace, or browser speech) | "loading" (the clip is
     * still downloading; t stays 0) | "idle" }.
     */
    position() {
      if (clock.kind === "voice" && typeof be.position === "function") {
        const t = be.position()
        const dur = typeof be.duration === "function" ? be.duration() : null
        if (t != null) return dur ? { part: clock.part, t, kind: "voice", dur } : { part: clock.part, t, kind: "voice" }
      }
      if (clock.kind === "loading") return { part: clock.part, t: 0, kind: "loading" }   // the clip is on its way: the highlight waits at its start
      return { part: clock.part, t: clock.kind === "idle" ? 0 : elapsedS(), kind: clock.kind === "idle" ? "idle" : "read" }
    },
    /** Why the last clip or utterance failed (for the Guide bubble's note), or null. */
    lastError() { return be.lastError || null },
  }
  return a
}
